export interface MaskDateInputOptions {
  includeTime?: boolean;
}

const DATE_DIGITS = 8;
const TIME_DIGITS = 4;

const digitsOf = (value: string) => value.replace(/\D/g, "");

// A separator is emitted as soon as the segment before it fills, so a filled segment always
// shows what comes next instead of looking like a finished entry.
const maskSegments = (digits: string, separatorAt: Record<number, string>) =>
  digits.split("").reduce((masked, digit, index) => masked + (separatorAt[index] ?? "") + digit, "") +
  (separatorAt[digits.length] ?? "");

const maskDateSegment = (digits: string) => maskSegments(digits, { 2: "-", 4: "-" });

const maskTimeSegment = (digits: string) => maskSegments(digits, { 2: ":" });

/**
 * Format raw user input as a fixed numeric date mask, discarding anything that cannot belong to
 * a date. Separators are owned by the mask and re-inserted at fixed offsets, so typed separators
 * are ignored rather than doubled.
 *
 * With `includeTime`, a `hh:mm` segment follows the date and a trailing `a`/`p` (case
 * insensitive) is accepted as the meridiem — but only once all four time digits are present.
 * Every other letter is dropped.
 *
 * @example
 * maskDateInput("08142026") // "08-14-2026"
 * maskDateInput("081420260330p", { includeTime: true }) // "08-14-2026 03:30 PM"
 */
export const maskDateInput = (raw: string, { includeTime = false }: MaskDateInputOptions = {}): string => {
  const digits = digitsOf(raw).slice(0, includeTime ? DATE_DIGITS + TIME_DIGITS : DATE_DIGITS);
  const masked = maskDateSegment(digits.slice(0, DATE_DIGITS));

  if (!includeTime || digits.length < DATE_DIGITS) return masked;

  const timeDigits = digits.slice(DATE_DIGITS);
  const withTime = `${masked} ${maskTimeSegment(timeDigits)}`;

  if (timeDigits.length < TIME_DIGITS) return withTime;

  // A newly typed a/p wins over the one already rendered, so the rendered AM/PM suffix is dropped
  // before looking for a bare letter — falling back to it keeps re-masked output idempotent.
  const rendered = raw.match(/[ap](?=m\b)/gi)?.at(-1);
  const meridiem =
    raw
      .replace(/[ap]m\b/gi, "")
      .match(/[ap]/gi)
      ?.at(-1) ?? rendered;
  return meridiem ? `${withTime} ${meridiem.toUpperCase()}M` : withTime;
};

/**
 * Parse a string produced by {@link maskDateInput} back to a Date. Returns null unless the mask
 * is complete (including the meridiem when `includeTime`) and the digits form a real calendar
 * date — a partially typed entry is not yet a date.
 */
export const parseMaskedDateInput = (
  masked: string,
  { includeTime = false }: MaskDateInputOptions = {}
): Date | null => {
  const match = includeTime
    ? masked.match(/^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}) (AM|PM)$/)
    : masked.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) return null;

  const [month, day, year, hours, minutes, meridiem] = match.slice(1);
  if (month === undefined || day === undefined || year === undefined) return null;

  const hour12 = hours === undefined ? 0 : parseInt(hours);
  if (includeTime && (hour12 < 1 || hour12 > 12)) return null;

  const hour = includeTime ? (hour12 % 12) + (meridiem === "PM" ? 12 : 0) : 0;
  const minute = minutes === undefined ? 0 : parseInt(minutes);
  if (minute > 59) return null;

  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), hour, minute);

  const isRealCalendarDate =
    date.getFullYear() === parseInt(year) &&
    date.getMonth() === parseInt(month) - 1 &&
    date.getDate() === parseInt(day);

  return isRealCalendarDate ? date : null;
};
